import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { MarkdownEditor, SenderSelect, SlugTextField } from '../broadcasts';
import { validateSurveyFlow } from '../../lib/surveyBranching';
import { SURVEY_SLUG_DEFS } from '../../lib/surveySlugs';
import type { AudienceScope } from '../../types/broadcast';
import type { SurveyChannel, SurveyDraftInput, SurveyLinkMode } from '../../types/survey';
import { previewSurveyAudience } from './api';
import { FieldLabel } from './fields';
import { SurveyAudienceSummary } from './SurveyAudienceSummary';

// Phase 6B B7 — the compose Send tab: link mode, close date, channels, invite
// content (with the survey-scoped slug picker — {survey_link}), sender modes,
// and a readiness-gated Send button. Send DISPATCH is Phase C; here the button
// is a stub (the draft is fully saved via autosave). The send-time gate mirrors
// what Phase C's /send will enforce: a clean flow (validateSurveyFlow) + a
// chosen channel with content + at least one reachable recipient (F6).

function hasBrand(scope: AudienceScope): boolean {
  return scope.brandSelection === 'all' || scope.brandSelection.brandAccountIds.length > 0;
}

function toLocalInputValue(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalInputValue(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function SendTab({
  draft,
  onChange,
  onLinkModeChange,
  sending,
  onSend,
}: {
  draft: SurveyDraftInput;
  onChange: (patch: Partial<SurveyDraftInput>) => void;
  onLinkModeChange: (mode: SurveyLinkMode) => void;
  sending: boolean;
  onSend: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const scopeKey = JSON.stringify(draft.audienceScope);
  const [debounced, setDebounced] = useState<AudienceScope>(draft.audienceScope);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(draft.audienceScope), 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scopeKey captures scope
  }, [scopeKey]);

  const audience = useQuery({
    queryKey: ['survey-audience', debounced],
    queryFn: () => previewSurveyAudience(debounced),
    enabled: hasBrand(debounced),
  });

  const wantsEmail = draft.channels.includes('email');
  const wantsSms = draft.channels.includes('sms');
  const toggleChannel = (ch: SurveyChannel) =>
    onChange({
      channels: draft.channels.includes(ch)
        ? draft.channels.filter((c) => c !== ch)
        : [...draft.channels, ch],
    });

  // Send-time readiness (the Phase C /send gate, surfaced now as a checklist).
  const flowIssues = validateSurveyFlow(draft.steps, draft.linkMode);
  const reachable = audience.data
    ? (wantsEmail ? audience.data.emailReachable : 0) + (wantsSms ? audience.data.smsReachable : 0)
    : 0;
  const checks: { ok: boolean; label: string }[] = [
    { ok: flowIssues.length === 0, label: 'Branching is valid' },
    { ok: draft.channels.length > 0, label: 'At least one channel selected' },
    { ok: !wantsEmail || (draft.emailSubject.trim() !== '' && draft.emailBodyMarkdown.trim() !== ''), label: 'Email subject + body provided' },
    { ok: !wantsSms || draft.smsBody.trim() !== '', label: 'SMS body provided' },
    { ok: reachable > 0, label: 'At least one reachable recipient' },
  ];
  const ready = checks.every((c) => c.ok);

  return (
    <div className="max-w-2xl space-y-6">
      {/* Link mode */}
      <section>
        <FieldLabel>Link mode</FieldLabel>
        <div className="space-y-2">
          <LinkModeOption
            mode="per_recipient"
            current={draft.linkMode}
            onSelect={onLinkModeChange}
            title="One link per recipient"
            desc="Each contact gets their own private link. Best for individual feedback (e.g. service surveys)."
          />
          <LinkModeOption
            mode="per_location"
            current={draft.linkMode}
            onSelect={onLinkModeChange}
            title="One link per location"
            desc="All contacts at a location share a link; the first to respond answers for the club. A “Your name” question is added automatically."
          />
        </div>
        {draft.linkMode === 'per_location' && (
          <p className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700">
            Note: anyone with a location's link can view its submitted answers. Share it only with the club's contacts.
          </p>
        )}
      </section>

      {/* Close date */}
      <section>
        <FieldLabel htmlFor="close-date">Close date (optional)</FieldLabel>
        <input
          id="close-date"
          type="datetime-local"
          value={toLocalInputValue(draft.closeDate)}
          onChange={(e) => onChange({ closeDate: fromLocalInputValue(e.target.value) })}
          className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
        />
        <p className="mt-1 text-xs text-slate-400">After this time the survey stops accepting responses.</p>
      </section>

      {/* Channels */}
      <section>
        <FieldLabel>Channels</FieldLabel>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={wantsEmail} onChange={() => toggleChannel('email')} /> Email
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={wantsSms} onChange={() => toggleChannel('sms')} /> SMS
          </label>
        </div>
      </section>

      {/* Email content */}
      {wantsEmail && (
        <section className="space-y-3 rounded-lg border border-slate-200 p-4">
          <h3 className="text-sm font-semibold text-slate-800">Invite email</h3>
          <div>
            <FieldLabel>Subject</FieldLabel>
            <SlugTextField value={draft.emailSubject} onChange={(v) => onChange({ emailSubject: v })} placeholder="We'd love your feedback" defs={SURVEY_SLUG_DEFS} />
          </div>
          <div>
            <FieldLabel>Body</FieldLabel>
            <MarkdownEditor value={draft.emailBodyMarkdown} onChange={(v) => onChange({ emailBodyMarkdown: v })} placeholder="Hi {first_name}, please complete our short survey: {survey_link}" rows={10} defs={SURVEY_SLUG_DEFS} />
            <p className="mt-1 text-xs text-slate-400">Add {'{survey_link}'} where the survey button should appear.</p>
          </div>
          <SenderSelect
            value={{
              emailFromMode: draft.emailFromMode,
              emailFromCustomName: draft.emailFromCustomName,
              emailReplyToMode: draft.emailReplyToMode,
              emailReplyToCustomEmail: draft.emailReplyToCustomEmail,
            }}
            onChange={(patch) => onChange(patch)}
          />
        </section>
      )}

      {/* SMS content */}
      {wantsSms && (
        <section className="space-y-3 rounded-lg border border-slate-200 p-4">
          <h3 className="text-sm font-semibold text-slate-800">Invite SMS</h3>
          <SlugTextField value={draft.smsBody} onChange={(v) => onChange({ smsBody: v })} multiline smsCounter rows={4} placeholder="Quick survey from your team: {survey_link}" defs={SURVEY_SLUG_DEFS} />
        </section>
      )}

      {/* Recipients summary (counts + preview) */}
      <section>
        <FieldLabel>Recipients</FieldLabel>
        <SurveyAudienceSummary scope={draft.audienceScope} linkMode={draft.linkMode} />
      </section>

      {/* Readiness + Send */}
      <section className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <h3 className="text-sm font-semibold text-slate-800">Before sending</h3>
        <ul className="mt-2 space-y-1 text-sm">
          {checks.map((c) => (
            <li key={c.label} className={c.ok ? 'text-slate-600' : 'text-slate-400'}>
              <span className={c.ok ? 'text-emerald-600' : 'text-slate-300'}>{c.ok ? '✓' : '○'}</span> {c.label}
            </li>
          ))}
        </ul>
        <div className="mt-4">
          <Button disabled={!ready || sending} onClick={() => setConfirming(true)}>
            {sending ? 'Sending…' : 'Send survey'}
          </Button>
        </div>
      </section>

      {confirming && (
        <ConfirmSend
          channels={draft.channels}
          closeDate={draft.closeDate}
          recipients={audience.data ? reachable : 0}
          linkMode={draft.linkMode}
          sending={sending}
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false);
            onSend();
          }}
        />
      )}
    </div>
  );
}

function ConfirmSend({
  channels,
  closeDate,
  recipients,
  linkMode,
  sending,
  onCancel,
  onConfirm,
}: {
  channels: SurveyChannel[];
  closeDate: string | null;
  recipients: number;
  linkMode: SurveyLinkMode;
  sending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30 p-4" onClick={onCancel}>
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold text-slate-800">Send this survey?</h3>
        <p className="mt-2 text-sm text-slate-600">
          Invites will go to <span className="font-semibold">{recipients}</span>{' '}
          {recipients === 1 ? 'recipient' : 'recipients'} via {channels.join(' + ') || 'no channel'}.
          {linkMode === 'per_location' ? ' One shared link per location.' : ' One private link each.'}
        </p>
        {closeDate && (
          <p className="mt-1 text-sm text-slate-500">
            Closes {new Date(closeDate).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })}.
          </p>
        )}
        <p className="mt-2 text-xs text-slate-400">Once sent, the survey is locked and can't be edited.</p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={sending}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={sending}>
            {sending ? 'Sending…' : 'Send now'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function LinkModeOption({
  mode,
  current,
  onSelect,
  title,
  desc,
}: {
  mode: SurveyLinkMode;
  current: SurveyLinkMode;
  onSelect: (m: SurveyLinkMode) => void;
  title: string;
  desc: string;
}) {
  const active = current === mode;
  return (
    <button
      type="button"
      onClick={() => onSelect(mode)}
      className={
        'flex w-full items-start gap-3 rounded-lg border p-3 text-left ' +
        (active ? 'border-kyck-500 ring-1 ring-kyck-500' : 'border-slate-200 hover:border-slate-300')
      }
    >
      <span className={'mt-0.5 flex h-4 w-4 items-center justify-center rounded-full border ' + (active ? 'border-kyck-600' : 'border-slate-300')}>
        {active && <span className="h-2 w-2 rounded-full bg-kyck-600" />}
      </span>
      <span>
        <span className="block text-sm font-medium text-slate-800">{title}</span>
        <span className="block text-xs text-slate-500">{desc}</span>
      </span>
    </button>
  );
}
